import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

/**
 * Carte « Serveur média (SFU) » du Super Admin (Paramètres).
 * Prépare le branchement d'un serveur média (SFU compatible LiveKit, hébergé sur
 * notre infrastructure) afin d'ouvrir les LIVES vidéo et les visioconférences à
 * grande échelle. Tant qu'il n'est pas activé, ZCall reste en WebRTC « maillage ».
 *
 * La clé et le secret restent côté plateforme : ils ne servent qu'à la fonction
 * serveur `zcall-token`, qui délivre des jetons éphémères aux participants.
 */
export default function SfuSettings() {
  const [loading, setLoading] = useState(true);
  const [rowId, setRowId] = useState<number | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [url, setUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('zifek')
        .select('id, sfu_enabled, sfu_provider, sfu_url, sfu_api_key, sfu_api_secret')
        .order('id', { ascending: true })
        .limit(1)
        .maybeSingle();
      if (data) {
        setRowId(Number(data.id));
        setEnabled(!!data.sfu_enabled);
        setUrl(String(data.sfu_url || ''));
        setApiKey(String(data.sfu_api_key || ''));
        setApiSecret(String(data.sfu_api_secret || ''));
      }
    } catch {
      /* garde les valeurs par défaut */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    const cleanUrl = url.trim();
    if (enabled && !cleanUrl) {
      setError('Une URL de serveur média (wss://…) est requise pour activer le SFU.');
      setSaving(false);
      return;
    }
    if (enabled && (!apiKey.trim() || !apiSecret.trim())) {
      setError('La clé et le secret du serveur média sont requis pour l\u2019activer.');
      setSaving(false);
      return;
    }
    try {
      const payload = {
        sfu_enabled: enabled,
        sfu_provider: 'livekit',
        sfu_url: cleanUrl,
        sfu_api_key: apiKey.trim(),
        sfu_api_secret: apiSecret.trim(),
      };
      if (rowId != null) {
        const { error: updErr } = await supabase.from('zifek').update(payload).eq('id', rowId);
        if (updErr) throw updErr;
      } else {
        const { error: insErr } = await supabase.from('zifek').insert(payload);
        if (insErr) throw insErr;
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\u2019enregistrement.');
    } finally {
      setSaving(false);
    }
  };

  const configured = !!url.trim() && !!apiKey.trim() && !!apiSecret.trim();
  const statusLabel = loading ? '…' : enabled ? 'Activé' : 'Désactivé';

  return (
    <section className="bg-background-50 border border-background-200/70 rounded-lg p-5">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
          <i className="ri-broadcast-line text-lg"></i>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="text-sm font-semibold text-foreground-900">Serveur média (SFU) — Lives &amp; grandes visio</h3>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
              enabled ? 'bg-green-100 text-green-700' : 'bg-background-100 text-foreground-500'
            }`}>
              {statusLabel}
            </span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
              configured ? 'bg-accent-100 text-accent-700' : 'bg-background-100 text-foreground-500'
            }`}>
              {configured ? 'Configuré' : 'Non configuré'}
            </span>
          </div>
          <p className="text-xs text-foreground-500 mb-4">
            Pour les diffusions en direct et les réunions à grande échelle, ZCall bascule automatiquement
            sur un serveur média (SFU compatible LiveKit, auto-hébergé sur votre serveur). Sans activation,
            ZCall reste en connexion directe « peer-to-peer » (idéal jusqu&apos;à ~6 participants).
          </p>

          {/* Activer / Désactiver */}
          <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-background-100 mb-4">
            <div className="min-w-0">
              <p className="text-xs font-semibold text-foreground-800">Activer le serveur média</p>
              <p className="text-[11px] text-foreground-500">
                Route les salles ZCall vers le SFU pour supporter les lives et les grandes réunions.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setEnabled((v) => !v)}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors cursor-pointer flex-shrink-0 ${
                enabled ? 'bg-primary-500' : 'bg-background-200'
              }`}
              aria-label={enabled ? 'Désactiver' : 'Activer'}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {/* Champs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="block text-[11px] font-semibold text-foreground-600 mb-1">URL du serveur média</label>
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="wss://live.zifek.fr"
                className="w-full h-10 px-3 rounded-lg border border-background-200/70 bg-background-50 text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 font-mono"
              />
              <p className="text-[10px] text-foreground-400 mt-1">Adresse WebSocket du SFU auto-hébergé (ex. LiveKit sur votre VPS).</p>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-foreground-600 mb-1">Clé API serveur média</label>
              <input
                type="text"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="APIxxxxxxx"
                className="w-full h-10 px-3 rounded-lg border border-background-200/70 bg-background-50 text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 font-mono"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-foreground-600 mb-1">Secret API serveur média</label>
              <div className="relative">
                <input
                  type={showSecret ? 'text' : 'password'}
                  value={apiSecret}
                  onChange={(e) => setApiSecret(e.target.value)}
                  placeholder="••••••••••••••••"
                  className="w-full h-10 px-3 pr-10 rounded-lg border border-background-200/70 bg-background-50 text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 font-mono"
                />
                <button
                  onClick={() => setShowSecret((v) => !v)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer"
                  title={showSecret ? 'Cacher' : 'Afficher'}
                >
                  <i className={`text-sm ${showSecret ? 'ri-eye-off-line' : 'ri-eye-line'}`}></i>
                </button>
              </div>
            </div>
          </div>

          {error && (
            <p className="text-xs text-red-600 mt-3 flex items-center gap-1.5">
              <i className="ri-error-warning-line"></i>{error}
            </p>
          )}

          <div className="flex items-center justify-between gap-3 mt-4 flex-wrap">
            <p className="text-[10px] text-foreground-400 flex items-center gap-1.5 max-w-md">
              <i className="ri-shield-check-line text-primary-500"></i>
              La clé et le secret restent côté plateforme et ne sont jamais exposés aux participants.
            </p>
            <button
              onClick={save}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer bg-foreground-950 text-background-50 hover:bg-foreground-800 transition-colors disabled:opacity-50"
            >
              {saving ? (
                <><i className="ri-loader-4-line animate-spin"></i>Enregistrement…</>
              ) : saved ? (
                <><i className="ri-check-line"></i>Enregistré</>
              ) : (
                <><i className="ri-save-line"></i>Enregistrer</>
              )}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}